import { useState } from 'react';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { useLazyMidasFlow } from '@rsos/reduxDataStore/src/features/midas/midasFlowApi';

/** Session duration in seconds — value expected by the international_callback flow. */
const SESSION_TTL_S = 30 * 60;
const SESSION_TTL_MS = SESSION_TTL_S * 1000;

/**
 * Manages the lifecycle of the International Callback modal.
 *
 * - `open` triggers the `international_callback` Midas flow and opens the modal.
 * - `close` dismisses the modal (the session remains cached for its full TTL).
 * - `state` exposes the current session data for the modal to render.
 */
const useIntlCallback = () => {
  const callerID = useSelectedCallerID();
  const [isOpen, setIsOpen] = useState(false);
  const [trigger, { data, isFetching, error, validationError }] =
    useLazyMidasFlow<'international_callback'>();

  const open = () => {
    setIsOpen(true);
    trigger({
      callflow: 'international_callback',
      variables: {
        caller_id: callerID ?? '',
        pin_timeout: SESSION_TTL_S,
        session_timeout: SESSION_TTL_S,
      },
      cacheTtlMs: SESSION_TTL_MS,
    });
  };

  const close = () => setIsOpen(false);

  const sessionError =
    validationError || error
      ? 'Unable to create callback session. Please try again.'
      : null;

  return {
    state: {
      isOpen,
      isCreatingSession: isFetching,
      pin: data?.job_id ?? null,
      bridgeNumber: data?.international_callback_phone ?? null,
      callerPhoneNumber: callerID,
      error: sessionError,
    },
    open,
    close,
  };
};

export default useIntlCallback;
